import { useSuspenseQuery } from "@tanstack/react-query";
import { useI18n } from "@wojo/localization";
import { getDeparturesSearchQuery } from "@wojo/services";
import { minBy } from "lodash";
import React from "react";
import { getCurrency } from "../../../hooks";
import { useTourPageContext } from "../../_client/context";
import { useLowestEstimatedPayment } from "./hooks";

type TourHeroLeadInPriceProps = {
    displayLowestLeadInPrice: boolean;
    displayLowestPayment: boolean;
};

export const TourHeroLeadInPrice: React.FC<TourHeroLeadInPriceProps> = ({
    displayLowestLeadInPrice,
    displayLowestPayment,
}) => {
    const { tourContent, defaultPaymentFrequency } = useTourPageContext();
    const { formatCurrency } = getCurrency();
    const { productPage } = useI18n("productPage");
    const { data: departures } = useSuspenseQuery({
        queryKey: ["lead-in-departures"],
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryFn: getDeparturesSearchQuery({
            hitsPerPage: 1000,
            page: 0,
            attributesToRetrieve: [
                "price",
                "salePrice",
                "discountAmount",
                "estimatedBiweeklyPayment",
                "estimatedMonthlyPayment",
            ],
            filters: {
                tourCode: tourContent.tourCode ?? "INVALID_TOUR_CODE",
            },
        }),
    });
    const lowestEstimatedPayment = useLowestEstimatedPayment(departures.hits);

    const hitWithMinPrice = minBy(departures.hits, (hit) => hit.price);
    const hitWithMinSalePrice = minBy(departures.hits, (hit) => hit.salePrice);
    const lowestPrice = Math.min(
        hitWithMinPrice?.price ?? 0,
        hitWithMinSalePrice?.salePrice ?? Number.POSITIVE_INFINITY,
    );

    if (lowestPrice === 0) {
        return null;
    }

    const displayPriceWithDiscount =
        (hitWithMinSalePrice?.salePrice ?? Number.POSITIVE_INFINITY) ===
            lowestPrice &&
        hitWithMinSalePrice?.salePrice !== hitWithMinSalePrice?.price;

    const PriceWithDiscount = () => (
        <>
            <span
                style={{
                    textDecoration: "line-through",
                }}
            >
                {formatCurrency(hitWithMinSalePrice?.price ?? 0, true)}
            </span>
            {` ${formatCurrency(hitWithMinSalePrice?.salePrice ?? 0, true)} `}
            {productPage.tourHero.heroSummary.pricing2.l()}
            {hitWithMinSalePrice?.discountAmount && (
                <span className="visually-hidden">
                    {productPage.tourHero.heroSummary.pricingAccessible.l({
                        discountAmount: formatCurrency(
                            hitWithMinSalePrice.discountAmount,
                            true,
                        ),
                    })}
                </span>
            )}
        </>
    );

    const LeadInPrice = () => (
        <>
            {` ${formatCurrency(lowestPrice, true)} `}
            {productPage.tourHero.heroSummary.pricing2.l()}
        </>
    );
    return (
        <>
            {displayLowestLeadInPrice ? (
                <>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    {productPage.tourHero.heroSummary.pricing1.l()}{" "}
                    {displayPriceWithDiscount ? (
                        <PriceWithDiscount />
                    ) : (
                        <LeadInPrice />
                    )}
                </>
            ) : null}
            {displayLowestPayment && lowestEstimatedPayment ? (
                <>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    {productPage.tourHero.heroSummary.payments.l({
                        paymentFrequency:
                            defaultPaymentFrequency?.toString().toLowerCase() ??
                            "",
                        lowestEstimatedPayment: formatCurrency(
                            lowestEstimatedPayment,
                            true,
                        ),
                    })}
                </>
            ) : null}
        </>
    );
};
